import { Anchor, Badge, Button, Card, Group, Stack, Table, Tabs, Text } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { RequestAccessModal } from "../components/Errors";
import { PageTitle } from "../components/Layout";
import { UsageMeters } from "../components/Usage";
import { formatDate, formatLimit, get, post, type AccessRequest, type LimitSpec, type Notice } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useMe, useRegistry } from "../lib/hooks";

const GROUPS: Record<string, string> = { platform: "Computing", storage: "Storage", transport: "Spin-Valley Qubits", skfitter: "Tight Binding (Slater-Koster)", tetris: "QTetris" };

export function LimitsTable({ limits, specs, onRequest, overrides }: {
  limits: Record<string, unknown>; specs: LimitSpec[]; onRequest?: (key: string) => void; overrides?: Record<string, unknown>;
}) {
  const products = Array.from(new Set(specs.map((s) => s.product)));
  return (
    <Stack>
      {products.map((prod) => (
        <div key={prod}>
          <Text fw={600} size="sm" mb={4}>{GROUPS[prod] ?? prod}</Text>
          <Table striped withTableBorder>
            <Table.Tbody>
              {specs.filter((s) => s.product === prod).map((s) => (
                <Table.Tr key={s.key}>
                  <Table.Td w="55%">
                    <Text size="sm">{s.label}</Text>
                    {s.help && <Text size="xs" c="dimmed">{s.help}</Text>}
                  </Table.Td>
                  <Table.Td>
                    {s.kind === "bool"
                      ? <Badge color={limits[s.key] ? "teal" : "gray"} variant="light">{limits[s.key] ? "Included" : "Not included"}</Badge>
                      : <Text size="sm">{formatLimit(s, limits[s.key])}</Text>}
                    {overrides && s.key in overrides && <Badge ml="xs" size="xs" variant="outline">personal</Badge>}
                  </Table.Td>
                  {onRequest && (
                    <Table.Td w={140} ta="right">
                      {!(s.kind === "bool" && limits[s.key]) && (
                        <Button size="compact-xs" variant="subtle" onClick={() => onRequest(s.key)}>
                          {s.kind === "bool" ? "Request access" : "Request more"}
                        </Button>
                      )}
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
      ))}
    </Stack>
  );
}

export function Account() {
  const { data: me } = useMe();
  const { data: reg } = useRegistry();
  const { hash } = useLocation();
  const [tab, setTab] = useState<string | null>(hash === "#notices" ? "notices" : "plan");
  const { accountUrl } = useAuth();
  const [requesting, setRequesting] = useState<string | null>(null);
  const qc = useQueryClient();
  const requests = useQuery({ queryKey: ["access-requests"], queryFn: () => get<AccessRequest[]>("/me/access-requests") });
  const notices = useQuery({ queryKey: ["notices"], queryFn: () => get<Notice[]>("/me/notifications") });
  const markRead = useMutation({ mutationFn: () => post("/me/notifications/read"), onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }) });

  useEffect(() => { if (hash === "#notices") setTab("notices"); }, [hash]);
  useEffect(() => {
    if (tab === "notices" && me?.unread_notifications) markRead.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, me?.unread_notifications]);

  if (!me || !reg) return null;
  const label = (k: string) => reg.limits.find((s) => s.key === k)?.label ?? k;
  return (
    <Stack>
      <PageTitle title="My plan & usage">
        <Badge size="lg" variant="light">{me.plan_name} plan</Badge>
      </PageTitle>
      {(me.organization || me.country) && (
        <Text size="sm" c="dimmed" data-testid="my-affiliation">
          {[me.organization, me.country].filter(Boolean).join(" · ")}
          {accountUrl && <> · <Anchor href={accountUrl} size="sm">change</Anchor></>}
        </Text>
      )}
      <UsageMeters me={me} />
      <Tabs value={tab} onChange={setTab}>
        <Tabs.List>
          <Tabs.Tab value="plan">What my plan includes</Tabs.Tab>
          <Tabs.Tab value="requests">My requests</Tabs.Tab>
          <Tabs.Tab value="notices">Notices {me.unread_notifications ? <Badge size="xs" ml={4}>{me.unread_notifications}</Badge> : null}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="plan" pt="md">
          <LimitsTable limits={me.limits} specs={reg.limits} overrides={me.overrides} onRequest={setRequesting} />
        </Tabs.Panel>
        <Tabs.Panel value="requests" pt="md">
          <Card withBorder>
            {!requests.data?.length ? <Text c="dimmed" size="sm">No requests yet.</Text> : (
              <Table>
                <Table.Thead><Table.Tr><Table.Th>For</Table.Th><Table.Th>Asked</Table.Th><Table.Th>Status</Table.Th><Table.Th>Note</Table.Th></Table.Tr></Table.Thead>
                <Table.Tbody>
                  {requests.data.map((r) => (
                    <Table.Tr key={r.id}>
                      <Table.Td>{label(r.limit_key)}{r.requested_value ? `: ${r.requested_value}` : ""}</Table.Td>
                      <Table.Td>{formatDate(r.created_at)}</Table.Td>
                      <Table.Td><Badge color={r.status === "approved" ? "teal" : r.status === "rejected" ? "red" : "gray"} variant="light">{r.status}</Badge></Table.Td>
                      <Table.Td><Text size="sm">{r.decision_note}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Card>
        </Tabs.Panel>
        <Tabs.Panel value="notices" pt="md">
          <Stack>
            {!notices.data?.length && <Text c="dimmed" size="sm">No notices.</Text>}
            {notices.data?.map((n) => (
              <Card withBorder key={n.id}>
                <Group justify="space-between">
                  <Text fw={600}>{n.title}</Text>
                  <Text size="xs" c="dimmed">{formatDate(n.created_at, true)}</Text>
                </Group>
                {n.body && <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>{n.body}</Text>}
              </Card>
            ))}
          </Stack>
        </Tabs.Panel>
      </Tabs>
      {requesting && <RequestAccessModal limitKey={requesting} opened onClose={() => setRequesting(null)} />}
    </Stack>
  );
}
